import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtDate, fmtTime, fmtMoney, fmtErr, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { getGeoAccurate, signalLabel } from "../operator/Dashboard";
import { MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhotoPicker } from "../../components/PhotoPicker";
import { ExpensePanel } from "../../components/ExpensePanel";
import { LogIssue } from "../../components/LogIssue";

export default function SubJobDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [showLogIssue, setShowLogIssue] = useState(false);
  const [showResched, setShowResched] = useState(false);
  const [resched, setResched] = useState({ date: "", reason: "" });
  const [showBlocked, setShowBlocked] = useState(false);
  const [blocked, setBlocked] = useState({ reason: "access", note: "" });
  const [sig, setSig] = useState(undefined);  // undefined = not attempted, null = no fix, number = accuracy m

  const reportBlocked = async () => {
    try {
      await api.post(`/visits/${id}/blocked`, blocked);
      toast.success("Reported — the office has been notified with the owner's contact info");
      setShowBlocked(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const load = () => api.get(`/visits/${id}`).then((r) => setV(r.data)).catch((e) => toast.error(fmtErr(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the route id changes; load is stable
  useEffect(() => { load(); }, [id]);

  if (!v) return <Layout title="Job"><p className="text-muted-foreground">Loading…</p></Layout>;

  const geoAction = async (path, msg) => {
    try {
      const g = await getGeoAccurate();
      setSig(g ? g.accuracy : null);
      await api.post(`/visits/${id}/${path}`, g || {});
      toast.success(msg); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const complete = async () => {
    try {
      const g = await getGeoAccurate(3000);
      await api.post(`/visits/${id}/complete`, { notes, photos, ...(g || {}) });
      toast.success("Job complete"); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const requestReschedule = async () => {
    try {
      await api.post(`/visits/${id}/reschedule`, resched);
      toast.success("Reschedule requested — the operator will confirm");
      setShowResched(false); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const myIn = v.my_sub_id ? v.my_check_in : v.check_in;
  const myOut = v.my_sub_id ? v.my_check_out : v.check_out;

  const verifyLabel = (s) => !s ? null : { verified: "Location verified", low_confidence: "Low-confidence GPS", out_of_range: "Out of range", unverified: "No GPS" }[s.verification];

  return (
    <Layout title={v.property_name}>
      <div className="space-y-6">
        <div className="card-flat p-6">
          <p className="text-sm text-muted-foreground">{v.address}</p>
          <p className="mt-1 text-sm font-semibold">{fmtDate(v.date)}</p>
          {v.access_notes && <p className="mt-3 rounded-md bg-muted p-3 text-sm"><span className="font-bold">Access:</span> {v.access_notes}</p>}
          {v.crew && (
            <div className="mt-3 rounded-md bg-muted p-3 text-sm" data-testid="team-panel">
              <p className="font-bold">Team job{v.team_name ? ` — ${v.team_name}` : ""}</p>
              {v.crew.map((c) => (
                <p key={c.id} className="mt-1 text-xs" data-testid={`teammate-${c.id}`}>
                  {c.id === v.my_sub_id ? "You" : c.name} — {c.check_out ? "checked out" : c.check_in ? `on site since ${fmtTime(c.check_in.time)}` : "not checked in yet"}
                </p>
              ))}
            </div>
          )}
          {v.reschedule_request && <p className="mt-3 rounded-md bg-secondary p-3 text-xs font-bold" data-testid="reschedule-pending">Reschedule to {fmtDate(v.reschedule_request.date)} requested — waiting on the operator</p>}
        </div>

        <section data-testid="sub-services">
          <p className="label-caps mb-2">Work to do</p>
          <div className="card-flat divide-y divide-border">
            {v.services.map((s, i) => <p key={`${s.name}-${i}`} className="p-4 font-semibold">{s.name}</p>)}
            {v.instructions.map((i) => (
              <div key={i.id} className="p-4" data-testid={`sub-instruction-${i.id}`}>
                <p className="font-semibold">+ {i.description}</p>
                {i.qa?.map((qa) => <p key={qa.q} className="mt-1 text-xs text-muted-foreground"><span className="font-semibold">{qa.q}</span> {qa.a}</p>)}
                {i.photo && <img src={photoSrc(i.photo)} alt="" className="mt-2 h-24 rounded-md object-cover" />}
              </div>
            ))}
            {v.services.length === 0 && v.instructions.length === 0 && <p className="p-4 text-sm text-muted-foreground">No details listed.</p>}
          </div>
        </section>

        {(myIn || myOut) && (
          <div className="card-flat space-y-1 p-4 text-sm" data-testid="sub-time-tracking">
            {myIn && <p className="flex items-center gap-2"><MapPin size={16} weight="bold" /> In {fmtTime(myIn.time)} · <span className="font-bold">{verifyLabel(myIn)}</span></p>}
            {myOut && <p className="flex items-center gap-2"><MapPin size={16} weight="bold" /> Out {fmtTime(myOut.time)} · <span className="font-bold">{verifyLabel(myOut)}</span></p>}
          </div>
        )}

        {["assigned", "in_progress", "scheduled"].includes(v.status) && (
          <div className="space-y-3">
            {sig !== undefined && (
              <p data-testid="sub-signal-indicator" className="text-sm font-semibold">Signal: {sig === null ? "no fix — saved without GPS" : signalLabel(sig)}</p>
            )}
            {!myIn && <button data-testid="sub-check-in-btn" onClick={() => geoAction("checkin", "Checked in")} className="btn-primary-big">Check in on site</button>}
            {myIn && !myOut && <button data-testid="sub-check-out-btn" onClick={() => geoAction("checkout", "Checked out")} className="h-14 w-full rounded-md border-2 border-primary text-lg font-bold text-primary">Check out</button>}
            {v.status === "in_progress" && (
              <div className="card-flat space-y-3 p-4" data-testid="sub-complete-panel">
                <textarea data-testid="sub-complete-notes" className="field-input h-24 w-full py-3" placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <PhotoPicker photos={photos} setPhotos={setPhotos} min={v.min_completion_photos ?? 2} testid="sub-add-photo-btn" cameraOnly={!!v.event_id} />
                <button data-testid="sub-complete-btn" onClick={complete} disabled={photos.length < (v.min_completion_photos ?? 2)} className="btn-primary-big !h-14 !text-base disabled:opacity-40">Mark complete</button>
              </div>
            )}
          </div>
        )}
        {!["invoiced", "paid", "cancelled"].includes(v.status) && (
          <ExpensePanel visitId={id} expenses={v.expenses || []} receiptRequired={v.receipt_required} onDone={load} testid="sub-expense" />
        )}
        {["assigned", "in_progress", "scheduled"].includes(v.status) && (
          <section data-testid="sub-blocked-section">
            <button data-testid="sub-blocked-btn" onClick={() => setShowBlocked(!showBlocked)}
              className="h-12 w-full rounded-md border-2 border-[hsl(24,90%,42%)] text-sm font-bold text-[hsl(24,90%,42%)]">Can't complete this visit</button>
            {showBlocked && (
              <div className="card-flat mt-2 space-y-2 p-4" data-testid="sub-blocked-form">
                <p className="text-xs text-muted-foreground">Tell the office why — they'll contact the owner and reschedule.</p>
                <div className="flex flex-wrap gap-2">
                  {[["access", "Access issue"], ["weather", "Weather"], ["customer", "Customer request"], ["safety", "Safety"], ["other", "Other"]].map(([k, label]) => (
                    <button key={k} type="button" data-testid={`blocked-reason-${k}`} onClick={() => setBlocked({ ...blocked, reason: k })}
                      className={`h-10 rounded-full border px-4 text-xs font-bold transition-colors ${blocked.reason === k ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{label}</button>
                  ))}
                </div>
                <input data-testid="blocked-note" className="field-input w-full" placeholder="Details (gate locked, dog out, no answer…)" value={blocked.note}
                  onChange={(e) => setBlocked({ ...blocked, note: e.target.value })} />
                <button data-testid="blocked-send" onClick={reportBlocked}
                  className="h-12 w-full rounded-md bg-[hsl(24,90%,42%)] text-sm font-bold text-white">Report blocked visit</button>
              </div>
            )}
          </section>
        )}
        {v.status === "blocked" && (
          <p className="rounded-md bg-[hsl(24,90%,42%)]/10 p-3 text-xs font-bold text-[hsl(24,90%,42%)]" data-testid="sub-blocked-banner">
            Reported blocked — the office is contacting the owner. You'll see it back here once rescheduled.
          </p>
        )}
        {["assigned", "scheduled"].includes(v.status) && !v.reschedule_request && (
          <section data-testid="sub-reschedule-section">
            <button data-testid="sub-reschedule-btn" onClick={() => setShowResched(!showResched)}
              className="h-12 w-full rounded-md border border-border text-sm font-bold">Request a reschedule</button>
            {showResched && (
              <div className="card-flat mt-2 space-y-2 p-4" data-testid="sub-reschedule-form">
                <p className="text-xs text-muted-foreground">Pick a date within 7 days of {fmtDate(v.date)} — the operator approves the move.</p>
                <input data-testid="sub-reschedule-date" type="date" className="field-input w-full" value={resched.date}
                  onChange={(e) => setResched({ ...resched, date: e.target.value })} />
                <input data-testid="sub-reschedule-reason" className="field-input w-full" placeholder="Reason (availability, another job…)" value={resched.reason}
                  onChange={(e) => setResched({ ...resched, reason: e.target.value })} />
                <button data-testid="sub-reschedule-send" onClick={requestReschedule} disabled={!resched.date}
                  className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-40">Send request</button>
              </div>
            )}
          </section>
        )}
        <section data-testid="sub-log-issue-section">
          <button data-testid="sub-log-issue-btn" onClick={() => setShowLogIssue(!showLogIssue)}
            className="h-12 w-full rounded-md bg-secondary text-sm font-bold text-secondary-foreground">Log an issue</button>
          {showLogIssue && (
            <div className="card-flat mt-2 p-4" data-testid="sub-log-issue-panel">
              <LogIssue propertyId={v.property_id} visitId={v.id} onDone={() => setShowLogIssue(false)} testid="sub-log-issue" />
            </div>
          )}
        </section>
        {["completed", "invoiced", "paid"].includes(v.status) && (
          <div className="card-flat p-6 text-center" data-testid="sub-done">
            <p className="font-heading text-xl font-bold text-primary">Done</p>
            <p className="text-sm text-muted-foreground">You'll be paid {fmtMoney(v.my_cost ?? v.sub_cost)} for this job.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
